import { getApiBaseUrl, joinUrl } from "./queryClient";

export interface ChatMessage {
  message: string;
  timestamp: string;
  sessionId: string;
  userType?: string;
}

export interface ChatResponse {
  response?: string;
  message?: string;
  error?: string;
}

export class ChatbotService {
  private static readonly CHAT_API_URL = "/chat/send";

  static async sendMessage(message: string, sessionId: string, userType?: string): Promise<ChatResponse> {
    try {
      const payload = {
        message,
        sessionId,
        timestamp: new Date().toISOString(),
        userType,
      };

      const baseUrl = getApiBaseUrl();
      const fullUrl = baseUrl ? joinUrl(baseUrl, this.CHAT_API_URL) : this.CHAT_API_URL;
      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include session cookie for authentication
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.message || 'Unknown error'}`);
      }

      const data = await response.json();
      
      // Log the response for debugging
      console.log('📨 Chat API Response:', {
        success: data.success,
        hasResponse: !!data.response,
        responseLength: data.response?.length,
        responsePreview: data.response?.substring(0, 100),
        dataKeys: Object.keys(data),
        fullData: data
      });
      
      if (!data.success) {
        throw new Error(data.error || "Failed to process message");
      }
      
      // Extract response - check multiple possible fields
      const responseText = data.response || 
                          data.message || 
                          data.text ||
                          data.data?.response ||
                          data.data?.message ||
                          "I'm sorry, I couldn't process your request. Please try again.";
      
      if (!responseText || responseText.trim() === '' || responseText === 'Message received successfully') {
        console.warn('⚠️ Chat response is empty or default message', { data });
        return {
          response: "I received your message, but I'm having trouble processing it. Please try rephrasing your question.",
        };
      }
      
      return {
        response: responseText,
      };
    } catch (error) {
      console.error("Error sending message to N8N:", error);
      
      return {
        error: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
      };
    }
  }

  static async testConnection(): Promise<boolean> {
    try {
      const response = await this.sendMessage("Hello", "test-session", "test");
      return !response.error;
    } catch (error) {
      console.error("Chatbot connection test failed:", error);
      return false;
    }
  }
} 